import React, { useCallback, useMemo, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import CancelButton from 'components/buttons/Cancel';
import DoneButton from 'components/buttons/Done';
import RowSpacer from 'components/RowSpacer';
import TermsOfService from 'components/TermsOfService';
import auth from 'lib/auth';
import useIsMobile from 'lib/useIsMobile';
import LoadingIndicator from 'components/LoadingIndicator';
import tracker from 'lib/tracker';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useApolloClient, useMutation } from '@apollo/client';
import moment from 'moment-timezone';
import { GET_PROFILE_INFO } from '../../schema/apolloQueries/commonQueries';
import { AGREE_TERMS_OF_SERVICE } from '../../schema/apolloMutations/agreeTermsOfService';

const useStyles = makeStyles((theme) => ({
  card: {
    padding: 16,
    overflow: 'auto',
    color: theme.palette.primary.contrastText,
  },
  light: {
    padding: '5px 9px 0',
    color: 'inherit',
  },
  termsOfService: {
    height: '33vh',
    overflowY: 'scroll',
  },
  termsActions: {},
}));

const tosLastUpdated = moment.tz(process.env.TOS_LAST_UPDATED, 'America/New_York');

const AcceptTermsDialog = () => {
  const styles = useStyles();
  const isMobile = useIsMobile();
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [acceptingTerms, setAcceptingTerms] = useState(false);

  const client = useApolloClient();

  const data = client.readQuery({
    query: GET_PROFILE_INFO,
  });

  const [agreeTermsOfService] = useMutation(AGREE_TERMS_OF_SERVICE, {
    refetchQueries: [GET_PROFILE_INFO],
  });

  const acceptTerms = useCallback(() => {
    if (!processing) {
      setError('');
      setProcessing(true);
      agreeTermsOfService()
        .then(() => {
          setAcceptingTerms(true);
        })
        .catch((e) => {
          try {
            const err = JSON.parse(e?.message);
            setError(err?.message);
            tracker.trackGoogleAnalyticsException(err, {});
          } catch (er) {
            setError(er?.message);
          }
          setAcceptingTerms(false);
        }).finally(() => {
          setProcessing(false);
        });
    }
  }, [setAcceptingTerms, processing]);

  const rejectTerms = useCallback(() => auth.logout(), []);
  const needsTermsAgreement = useMemo(() => {
    if (data?.getProfile) {
      const termsAccepted = data?.getProfile?.personInfo?.termsAccepted;
      if (!moment(termsAccepted).isValid()) {
        return true;
      }

      return moment.tz(termsAccepted, 'America/New_York').isBefore(tosLastUpdated);
    }
    return false;
  }, [data?.getProfile]);

  return data?.getProfile?.loggedIn ? (
    <Dialog modal fullWidth fullScreen={isMobile} open={needsTermsAgreement}>
      <DialogTitle className={styles.light}>
        <Typography className={styles.light} variant='h6'>
          Our Terms of Service have been updated
        </Typography>
        <Typography className={styles.light} variant='caption'>
          Please review and accept the updated agreement
        </Typography>
      </DialogTitle>
      <DialogContent className={styles.termsOfService}>
        <Typography color='error' variant='subtitle1'>
          {error}
        </Typography>
        <TermsOfService />
      </DialogContent>
      <DialogActions className={styles.termsActions}>
        <CancelButton
          disabled={acceptingTerms}
          label='Reject'
          secondary
          onClick={rejectTerms}
        />
        <RowSpacer />
        <DoneButton
          disabled={acceptingTerms}
          label={processing ? <LoadingIndicator size={20} /> : 'Accept'}
          primary
          onClick={acceptTerms}
        />
      </DialogActions>
    </Dialog>
  ) : null;
};

export default AcceptTermsDialog;
